// Enhanced Progress Metrics Component
// Displays detailed challenge progress information including timeline, points, and daily progress

import React from 'react';
import { ChallengeData, ChallengeProgressData, calculateDetailedProgressMetrics, formatProgressMetrics, formatValue, getUnitDisplay } from './ChallengeProgressView';

export interface EnhancedProgressMetricsProps {
    challenge: ChallengeData;
    progressData: ChallengeProgressData;
    className?: string;
    showDetailedBreakdown?: boolean;
}

export const EnhancedProgressMetrics = ({
    challenge,
    progressData,
    className = '',
    showDetailedBreakdown = true
}: EnhancedProgressMetricsProps) => {
    const metrics = calculateDetailedProgressMetrics(challenge, progressData);
    const formattedMetrics = formatProgressMetrics(challenge, progressData);
    const unit = (challenge as any).measurementUnit || challenge.targetUnit || getUnitDisplay(challenge.metric);

    return (
        <div className={`enhanced-progress-metrics ${className}`}>
            {/* Timeline Section */}
            <div className="timeline-section mb-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Timeline</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        {metrics.timeline.daysElapsed} of {metrics.timeline.totalDays} days
                    </span>
                </div>
                <div className="relative">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                            className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(100, metrics.timeline.progressPercentage)}%` }}
                        />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>0</span>
                        <span className="font-medium">{metrics.timeline.daysRemaining} days left</span>
                        <span>{metrics.timeline.totalDays}</span>
                    </div>
                </div>
            </div>

            {/* Points Section */}
            <div className="points-section mb-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Points Earned</span>
                    <span className="text-lg font-bold text-green-600 dark:text-green-400">
                        {metrics.points.totalEarned}
                    </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                    * Each day is {metrics.points.pointsPerDay} points, {metrics.overallProgress.completedDays} days completed
                </div>
            </div>

            {/* Overall Progress Section */}
            <div className="overall-progress-section mb-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Progress</span>
                    <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {metrics.points.completionRate.toFixed(1)}%
                    </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                    {metrics.points.totalEarned}/{metrics.points.totalPossible} points
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    * {metrics.timeline.totalDays} days × {metrics.points.pointsPerDay} points = {metrics.points.totalPossible} total
                </div>
            </div>

            {/* Today's Progress Section */}
            <div className="today-progress-section mb-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Today's Progress</span>
                    <span className={`text-lg font-bold ${metrics.todayProgress.isCompleted ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                        {metrics.todayProgress.progressPercentage.toFixed(0)}%
                    </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    {formatValue(metrics.todayProgress.currentValue, challenge.metric, unit)} completed out of {formatValue(metrics.todayProgress.targetValue, challenge.metric, unit)}
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                        className={`h-2 rounded-full transition-all duration-300 ${metrics.todayProgress.isCompleted
                            ? 'bg-green-600 dark:bg-green-500'
                            : 'bg-orange-500 dark:bg-orange-400'
                            }`}
                        style={{ width: `${Math.min(100, metrics.todayProgress.progressPercentage)}%` }}
                    />
                </div>
                {metrics.todayProgress.isCompleted && (
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1 font-medium">
                        ✓ Completed! +{metrics.points.pointsPerDay} points earned
                    </div>
                )}
            </div>

            {/* Detailed Breakdown (Optional) */}
            {showDetailedBreakdown && (
                <div className="detailed-breakdown border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                            <span className="text-gray-500 dark:text-gray-400">Completed Days:</span>
                            <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">
                                {metrics.overallProgress.completedDays}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-500 dark:text-gray-400">Success Rate:</span>
                            <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">
                                {metrics.overallProgress.successRate.toFixed(1)}%
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-500 dark:text-gray-400">Days Elapsed:</span>
                            <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">
                                {metrics.timeline.daysElapsed}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-500 dark:text-gray-400">Days Remaining:</span>
                            <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">
                                {metrics.timeline.daysRemaining}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnhancedProgressMetrics;
